/**
 * Performance bar chart showing top gainers/losers
 *
 * STATUS: PRESERVED BUT NOT CURRENTLY USED
 * See index.ts for full explanation.
 *
 * This component renders a horizontal bar chart showing performance.
 * Supports both percentage and absolute dollar value modes.
 * Green bars for gains, red bars for losses.
 * Potential uses: Admin dashboard, mobile views, email digests.
 */
import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import type { HoldingPerformance } from '../../types/analytics';

interface PerformanceBarChartProps {
  data: HoldingPerformance[];
  height?: number;
  showAbsolute?: boolean; // Show absolute values vs percentage
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value: number): string => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: HoldingPerformance & { displayValue: number };
  }>;
}

const CustomTooltip = ({ active, payload }: TooltipProps) => {
  if (!active || !payload || !payload.length) return null;

  const item = payload[0].payload;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="text-sm font-bold text-gray-900 mb-1">{item.symbol}</p>
      <p className="text-xs text-gray-500 mb-2">{item.account_name}</p>
      <p className="text-sm text-gray-600">
        Cost: {formatCurrency(item.cost_basis)}
      </p>
      {item.current_value !== null && (
        <p className="text-sm text-gray-600">
          Value: {formatCurrency(item.current_value)}
        </p>
      )}
      {item.gain_loss !== null && (
        <p className={`text-sm font-medium ${item.gain_loss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {formatCurrency(item.gain_loss)} ({formatPercent(item.gain_loss_pct ?? 0)})
        </p>
      )}
    </div>
  );
};

export function PerformanceBarChart({
  data,
  height = 300,
  showAbsolute = false,
}: PerformanceBarChartProps) {
  const chartData = useMemo(() => {
    return data.map(item => ({
      ...item,
      displayValue: showAbsolute ? (item.gain_loss ?? 0) : (item.gain_loss_pct ?? 0),
    }));
  }, [data, showAbsolute]);

  // Calculate domain with some padding
  const yDomain = useMemo(() => {
    if (!chartData.length) return [-10, 10];

    const values = chartData.map(d => d.displayValue);
    const min = Math.min(...values, 0);
    const max = Math.max(...values, 0);
    const padding = Math.abs(max - min) * 0.1 || 5;

    return [min - padding, max + padding];
  }, [chartData]);

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="symbol"
          tick={{ fontSize: 11, fill: '#6b7280' }}
          tickLine={false}
          axisLine={{ stroke: '#e5e7eb' }}
          interval={0}
          angle={-45}
          textAnchor="end"
          height={60}
        />
        <YAxis
          domain={yDomain}
          tick={{ fontSize: 12, fill: '#6b7280' }}
          tickFormatter={showAbsolute ? formatCurrency : formatPercent}
          tickLine={false}
          axisLine={{ stroke: '#e5e7eb' }}
          width={80}
        />
        <Tooltip content={<CustomTooltip />} />
        <ReferenceLine y={0} stroke="#9ca3af" />
        <Bar dataKey="displayValue" radius={[4, 4, 0, 0]}>
          {chartData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.displayValue >= 0 ? '#10b981' : '#ef4444'}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default PerformanceBarChart;
